import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiShield, FiTarget, FiTrendingUp } from 'react-icons/fi';
import { jsPDF } from 'jspdf';

const DefensivePublicationTDI = () => {
  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    let yPosition = 20;
    
    // Header with branding
    doc.setFillColor(147, 51, 234); // Purple
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Snap Chart AI - Prior Art Documentation', margin, 22);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Defensive Publication - TDI', margin, 30);
    
    yPosition = 50;
    doc.setTextColor(0, 0, 0);
    
    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Defensive Publication - TDI', margin, yPosition);
    yPosition += 10;
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const subtitle = doc.splitTextToSize('Trader Discipline Index (TDI): Behavioral Discipline Scoring for Trading Decision Quality, Risk Adherence, and Process Integrity', maxWidth);
    doc.text(subtitle, margin, yPosition);
    yPosition += subtitle.length * 6 + 8;
    
    // Metadata
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Applicant / Publisher:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Snap-Chart AI (https://snap-chart.ai/)', margin + 40, yPosition);
    yPosition += 6;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Product Name:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('TDI (Trader Discipline Index)', margin + 30, yPosition);
    yPosition += 6;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Publication Date:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('December 14th, 2025', margin + 35, yPosition);
    yPosition += 6;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Jurisdiction:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Global (Defensive Prior Art)', margin + 25, yPosition);
    yPosition += 12;
    
    // Abstract
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Abstract', margin, yPosition);
    yPosition += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const abstractText = doc.splitTextToSize(
      'This publication describes a system and method for generating a Trader Discipline Index (TDI), a behavioral intelligence score that evaluates trading decision quality and process integrity over time. The TDI is derived from trader actions and journaled decisions (pre-trade intent, execution behavior, and post-trade review), including rule adherence, risk consistency, and behavioral stability across sessions. The TDI is used to guide discipline coaching, enforce risk governance strictness, and provide institutional-style oversight for individuals and teams—without generating trade signals or predicting markets.',
      maxWidth
    );
    doc.text(abstractText, margin, yPosition);
    yPosition += abstractText.length * 4 + 8;
    
    // Field of the Invention
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Field of the Invention', margin, yPosition);
    yPosition += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const fields = [
      '• Behavioral finance scoring systems',
      '• Trading performance governance and accountability',
      '• AI-assisted risk discipline measurement',
      '• Decision-quality analytics distinct from outcome-based analytics',
      '• Adaptive process enforcement systems'
    ];
    fields.forEach(field => {
      doc.text(field, margin, yPosition);
      yPosition += 5;
    });
    yPosition += 5;
    
    // Check if we need a new page
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }
    
    // Background
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Background', margin, yPosition);
    yPosition += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const backgroundText = doc.splitTextToSize(
      'Many traders and tools measure performance primarily by outcomes (P/L, win rate). Institutions additionally measure process quality because good outcomes can occur from bad process (luck), bad outcomes can occur from good process (variance), and long-term survivability depends on risk and discipline integrity. There exists a need for a standardized discipline index that measures decision quality and adherence to risk rules, enabling consistent coaching and governance independent of market outcomes.',
      maxWidth
    );
    doc.text(backgroundText, margin, yPosition);
    yPosition += backgroundText.length * 4 + 10;
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('© 2025 Quantum AI Fintech Corporation. All rights reserved.', margin, 285);
    doc.text('This document constitutes defensive prior art publication.', margin, 290);
    
    doc.save('Snap-Chart-AI-Defensive-Publication-TDI.pdf');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-gray-900/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/legal/defensive-publications" 
            className="flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors"
          >
            <FiArrowLeft className="text-lg" />
            <span>Back to Defensive Publications</span>
          </Link>
          <button
            onClick={generatePDF}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 rounded-lg text-cyan-400 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-cyan-500/50 transition-all"
          >
            <FiDownload className="text-lg" />
            <span>Download PDF</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Title Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
              <FiTarget className="text-2xl text-cyan-400" />
            </div>
            <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 text-sm font-medium rounded-full border border-cyan-500/30">
              Defensive Prior Art
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
            Defensive Publication - TDI
          </h1>
          <p className="text-xl text-gray-400 leading-relaxed">
            Trader Discipline Index (TDI): Behavioral Discipline Scoring for Trading Decision Quality, Risk Adherence, and Process Integrity
          </p>
        </div>

        {/* Metadata Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
          <div className="p-4 bg-gray-800/50 rounded-xl border border-white/10">
            <p className="text-sm text-gray-500 mb-1">Applicant / Publisher</p>
            <p className="text-white font-medium">Snap-Chart AI</p>
            <a href="https://snap-chart.ai/" className="text-cyan-400 text-sm hover:underline">https://snap-chart.ai/</a>
          </div>
          <div className="p-4 bg-gray-800/50 rounded-xl border border-white/10">
            <p className="text-sm text-gray-500 mb-1">Product Name</p>
            <p className="text-white font-medium">TDI</p>
            <p className="text-gray-400 text-sm">Trader Discipline Index</p>
          </div>
          <div className="p-4 bg-gray-800/50 rounded-xl border border-white/10">
            <p className="text-sm text-gray-500 mb-1">Publication Date</p>
            <p className="text-white font-medium">December 14th, 2025</p>
          </div>
          <div className="p-4 bg-gray-800/50 rounded-xl border border-white/10">
            <p className="text-sm text-gray-500 mb-1">Jurisdiction</p>
            <p className="text-white font-medium">Global (Defensive Prior Art)</p>
          </div>
        </div>

        {/* Main Content */}
        <article className="prose prose-invert prose-lg max-w-none [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:bg-gradient-to-r [&_h2]:from-purple-400 [&_h2]:to-cyan-400 [&_h2]:bg-clip-text [&_h2]:text-transparent [&_h2]:mb-6 [&_h2]:mt-12 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-white [&_h3]:mb-4 [&_h3]:mt-8 [&_p]:text-gray-300 [&_p]:leading-relaxed [&_p]:mb-4 [&_ul]:space-y-4 [&_li]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-6">
          
          <h2>Abstract</h2>
          <p>
            This publication describes a system and method for generating a Trader Discipline Index (TDI), a behavioral intelligence score that evaluates trading decision quality and process integrity over time. The TDI is derived from trader actions and journaled decisions (pre-trade intent, execution behavior, and post-trade review), including rule adherence, risk consistency, and behavioral stability across sessions. The TDI is used to guide discipline coaching, enforce risk governance strictness, and provide institutional-style oversight for individuals and teams—without generating trade signals or predicting markets.
          </p>

          <h2>Field of the Invention</h2>
          <p>The present disclosure relates to:</p>
          <ul>
            <li>Behavioral finance scoring systems</li>
            <li>Trading performance governance and accountability</li>
            <li>AI-assisted risk discipline measurement</li>
            <li>Decision-quality analytics distinct from outcome-based analytics</li>
            <li>Adaptive process enforcement systems</li>
          </ul>

          <h2>Background</h2>
          <p>
            Many traders and tools measure performance primarily by outcomes (P/L, win rate). Institutions additionally measure process quality because:
          </p>
          <ul>
            <li>Good outcomes can occur from bad process (luck)</li>
            <li>Bad outcomes can occur from good process (variance)</li>
            <li>Long-term survivability depends on risk and discipline integrity</li>
          </ul>
          <p>
            There exists a need for a standardized discipline index that measures decision quality and adherence to risk rules, enabling consistent coaching and governance independent of market outcomes.
          </p>

          <h2>Summary of the System (High-Level)</h2>
          <p>The system comprises a discipline scoring engine that:</p>

          <h3>1) Ingests behavioral and process signals</h3>
          <ul>
            <li>Pre-trade plan completeness and clarity</li>
            <li>Risk definition quality (stop logic, max loss, exposure constraints)</li>
            <li>Execution behavior (chasing, stop moves, size changes, rapid re-entries)</li>
            <li>Session stability (over-trading detection, revenge patterns, cooldown compliance)</li>
            <li>Post-trade review consistency (accountability, lesson capture, rule correction)</li>
          </ul>

          <h3>2) Computes a multi-factor discipline score (TDI)</h3>
          <p>The TDI may incorporate categories such as:</p>
          <ul>
            <li>Rule Adherence Score (plan compliance, checklist integrity)</li>
            <li>Risk Consistency Score (stable risk per trade, drawdown respect)</li>
            <li>Impulse Control Score (tilt markers, chase behavior, late entries)</li>
            <li>Execution Integrity Score (stop discipline, size discipline, deviation frequency)</li>
            <li>Review & Learning Score (review completion, error classification, corrective drills)</li>
          </ul>

          <h3>3) Tracks TDI over time and across contexts</h3>
          <ul>
            <li>Rolling 7/30/90-day TDI</li>
            <li>Session-based deltas (before/after losses, after wins, during volatility spikes)</li>
            <li>Strategy-specific discipline trends (e.g., better discipline on A+ setups)</li>
          </ul>

          <h3>4) Uses TDI as a governance and coaching control signal</h3>
          <ul>
            <li>Adjusts strictness of a risk officer layer (approve / wait / no-trade)</li>
            <li>Triggers cooldown recommendations (time-based or trade-count-based)</li>
            <li>Recommends risk reductions when discipline deteriorates</li>
            <li>Highlights the highest-impact behavioral leaks to correct first</li>
          </ul>

          <h2>Behavioral Intelligence Layer (Conceptual)</h2>
          <p>
            The system may derive a Discipline Profile Vector (or similar representation) that captures the trader's behavioral tendencies (e.g., "risk escalation after loss," "rule drift during high volatility," "over-trading in low-quality conditions"). This representation may shape:
          </p>
          <ul>
            <li>The tone and firmness of interventions</li>
            <li>The governance thresholds applied</li>
            <li>The pacing and intensity of corrective coaching</li>
          </ul>
          <p className="text-yellow-400/80 italic">
            Note: Specific algorithms, thresholds, and weighting are implementation-specific and intentionally undisclosed.
          </p>

          <h2>Decision Authority Model</h2>
          <p>The TDI system:</p>
          <ul>
            <li>Does not provide trade entries/exits</li>
            <li>Does not forecast price movement</li>
            <li>Does not guarantee results</li>
          </ul>
          <p>Instead, it:</p>
          <ul>
            <li>Measures discipline and decision quality</li>
            <li>Detects behavioral instability early</li>
            <li>Informs risk governance strictness and coaching interventions</li>
            <li>Prioritizes capital preservation through process enforcement</li>
          </ul>

          <h2>Human–Machine Interface</h2>
          <p>The system may include:</p>
          <ul>
            <li>A dashboard showing TDI trend, breakdown, and drivers</li>
            <li>Session alerts ("discipline deterioration detected—reduce risk / pause")</li>
            <li>Journal prompts and accountability checklists</li>
            <li>Exportable reports for coaches, prop firms, or internal team reviews</li>
          </ul>

          <h2>Distinguishing Characteristics</h2>
          <p>Key differentiators include:</p>
          <ul>
            <li>Measures decision quality and discipline, not just outcomes</li>
            <li>Behavioral scoring derived from process artifacts (plans, actions, reviews)</li>
            <li>Adaptive governance: score influences enforcement strictness</li>
            <li>Institutional "risk officer + discipline index" paradigm for individuals/teams</li>
            <li>Separation between discipline scoring and market prediction</li>
          </ul>

          <h2>Intended Use Cases</h2>
          <ul>
            <li>Traders building consistent process discipline</li>
            <li>Prop firm evaluations and coaching systems</li>
            <li>Trading teams requiring standardized accountability metrics</li>
            <li>Educational trading environments and mentoring programs</li>
            <li>Risk compliance overlays for funds and desks (process audit layer)</li>
          </ul>

          <h2>Disclaimer</h2>
          <p>This publication intentionally omits:</p>
          <ul>
            <li>Model architectures and training methods</li>
            <li>Prompt structures</li>
            <li>Exact scoring formulas and thresholds</li>
            <li>Data schemas and storage layout</li>
            <li>Implementation details</li>
          </ul>
          <p className="text-yellow-400/80 italic">
            These elements remain protected as trade secrets.
          </p>

          <h2>Purpose of Publication</h2>
          <div className="p-6 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 rounded-xl border border-purple-500/30 mt-6">
            <p className="text-lg text-white font-medium mb-0">
              This document is published as defensive prior art to prevent the patenting of the described concepts by third parties.
            </p>
          </div>
        </article>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <p className="text-gray-500 text-sm text-center">
            © 2025 Quantum AI Fintech Corporation. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DefensivePublicationTDI;
